import React, { useId } from 'react';
import './InjectedSyringeCloudLogo.css';

export type InjectedSyringeCloudLogoVariant = 'hero' | 'md' | 'sm';

export interface InjectedSyringeCloudLogoProps {
  className?: string;
  variant?: InjectedSyringeCloudLogoVariant;
  animated?: boolean;
  title?: string;
}

/**
 * Injected Atlas brand mark — medical syringe delivering payload into a volumetric cloud.
 */
export const InjectedSyringeCloudLogo: React.FC<InjectedSyringeCloudLogoProps> = ({
  className = '',
  variant = 'hero',
  animated = true,
  title = 'Injected Atlas',
}) => {
  const uid = useId().replace(/:/g, '');
  const id = (name: string) => `ix-${name}-${uid}`;
  const ref = (name: string) => `url(#${id(name)})`;

  return (
    <svg
      className={`injected-syringe-logo injected-syringe-logo--${variant}${animated ? '' : ' injected-syringe-logo--static'} ${className}`.trim()}
      viewBox="0 0 860 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <defs>
        <radialGradient id={id('vignette')} cx="62%" cy="48%" r="58%">
          <stop offset="0%" stopColor="#3a1214" stopOpacity="0.55" />
          <stop offset="42%" stopColor="#14080a" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#050506" stopOpacity="0" />
        </radialGradient>

        <linearGradient id={id('glass')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f4f7fb" stopOpacity="0.55" />
          <stop offset="18%" stopColor="#ffffff" stopOpacity="0.16" />
          <stop offset="48%" stopColor="#9aa6b8" stopOpacity="0.08" />
          <stop offset="78%" stopColor="#ffffff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#6b7688" stopOpacity="0.28" />
        </linearGradient>
        <linearGradient id={id('glass-edge')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="40%" stopColor="#c5ceda" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id={id('fluid')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff8a80" />
          <stop offset="18%" stopColor="#ff5252" />
          <stop offset="55%" stopColor="#c62828" />
          <stop offset="100%" stopColor="#6d0d0d" />
        </linearGradient>
        <linearGradient id={id('fluid-sheen')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id('steel')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="28%" stopColor="#cbd5e1" />
          <stop offset="52%" stopColor="#64748b" />
          <stop offset="78%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
        <linearGradient id={id('steel-dark')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="50%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>
        <linearGradient id={id('plastic')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a3a44" />
          <stop offset="45%" stopColor="#16161c" />
          <stop offset="100%" stopColor="#0a0a0e" />
        </linearGradient>
        <linearGradient id={id('rubber')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a2a2e" />
          <stop offset="50%" stopColor="#0c0c0e" />
          <stop offset="100%" stopColor="#1a1a1e" />
        </linearGradient>

        <radialGradient id={id('lobe-a')} cx="38%" cy="32%" r="68%">
          <stop offset="0%" stopColor="#3a3d48" />
          <stop offset="55%" stopColor="#1a1c24" />
          <stop offset="100%" stopColor="#0c0d12" />
        </radialGradient>
        <radialGradient id={id('lobe-b')} cx="42%" cy="28%" r="70%">
          <stop offset="0%" stopColor="#4a4e5c" />
          <stop offset="60%" stopColor="#22242e" />
          <stop offset="100%" stopColor="#101218" />
        </radialGradient>
        <radialGradient id={id('lobe-c')} cx="30%" cy="30%" r="72%">
          <stop offset="0%" stopColor="#2e313c" />
          <stop offset="100%" stopColor="#12141a" />
        </radialGradient>
        <radialGradient id={id('bloom')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.95" />
          <stop offset="28%" stopColor="#e53935" stopOpacity="0.55" />
          <stop offset="62%" stopColor="#8e1b1b" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#5c0000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('bloom-core')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff5f5" stopOpacity="0.9" />
          <stop offset="35%" stopColor="#ff5252" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#c62828" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id('rim')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.28" />
          <stop offset="45%" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#ff8a80" stopOpacity="0.12" />
        </linearGradient>
        <linearGradient id={id('jet')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff8a80" stopOpacity="0.15" />
          <stop offset="35%" stopColor="#ff5252" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0.85" />
        </linearGradient>

        <filter id={id('cloud-soft')} x="-12%" y="-18%" width="124%" height="140%">
          <feDropShadow dx="0" dy="18" stdDeviation="16" floodColor="#000" floodOpacity="0.55" />
        </filter>
        <filter id={id('bloom-blur')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        <filter id={id('floor')} x="-20%" y="-20%" width="140%" height="160%">
          <feGaussianBlur stdDeviation="10" />
        </filter>

        <clipPath id={id('cloud-clip')}>
          <path d="M478 248 C462 206 498 162 552 150 C586 112 662 108 706 146 C758 126 822 158 824 212 C856 236 844 296 794 316 C806 358 742 386 678 364 C634 396 552 378 524 336 C478 348 452 304 478 248 Z" />
        </clipPath>
        <clipPath id={id('barrel-clip')}>
          <rect x="132" y="188" width="214" height="52" rx="6" />
        </clipPath>
        <clipPath id={id('glass-sheen-clip')}>
          <rect x="136" y="190" width="206" height="48" rx="5" />
        </clipPath>
      </defs>

      <ellipse cx="560" cy="230" rx="340" ry="190" fill={ref('vignette')} />

      {/* Contact shadow */}
      <ellipse cx="430" cy="392" rx="250" ry="16" fill="#000" opacity="0.35" filter={ref('floor')} />

      {/* Volumetric cloud */}
      <g className="injected-syringe-logo__cloud" filter={ref('cloud-soft')}>
        <g clipPath={ref('cloud-clip')}>
          <rect x="430" y="90" width="450" height="320" fill="#12141a" />
          <ellipse cx="620" cy="250" rx="150" ry="92" fill={ref('lobe-a')} />
          <ellipse cx="548" cy="214" rx="92" ry="70" fill={ref('lobe-b')} />
          <ellipse cx="710" cy="198" rx="108" ry="74" fill={ref('lobe-b')} opacity="0.92" />
          <ellipse cx="760" cy="262" rx="78" ry="58" fill={ref('lobe-c')} />
          <ellipse cx="590" cy="300" rx="100" ry="52" fill={ref('lobe-c')} opacity="0.85" />
          <ellipse cx="680" cy="168" rx="70" ry="28" fill="#ffffff" opacity="0.07" />
          <ellipse cx="560" cy="188" rx="46" ry="16" fill="#ffffff" opacity="0.05" />

          <g className="injected-syringe-logo__bloom" filter={ref('bloom-blur')}>
            <ellipse cx="512" cy="236" rx="78" ry="52" fill={ref('bloom')}>
              {animated && (
                <animate attributeName="opacity" values="0.72;1;0.72" dur="3.6s" repeatCount="indefinite" />
              )}
            </ellipse>
            <ellipse cx="524" cy="240" rx="28" ry="18" fill={ref('bloom-core')}>
              {animated && (
                <animate attributeName="opacity" values="0.55;0.95;0.55" dur="2.8s" repeatCount="indefinite" />
              )}
            </ellipse>
          </g>

          {/* Internal diffusion veins */}
          <path
            d="M508 236 C548 228 590 246 640 232"
            stroke="#e53935"
            strokeOpacity="0.22"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
          <path
            d="M514 242 C560 268 620 258 690 274"
            stroke="#ff5252"
            strokeOpacity="0.12"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </g>
        <path
          d="M478 248 C462 206 498 162 552 150 C586 112 662 108 706 146 C758 126 822 158 824 212 C856 236 844 296 794 316 C806 358 742 386 678 364 C634 396 552 378 524 336 C478 348 452 304 478 248 Z"
          fill="none"
          stroke={ref('rim')}
          strokeWidth="1.25"
        />
      </g>

      {/* Syringe — horizontal, needle entering the cloud */}
      <g className="injected-syringe-logo__syringe">
        {/* Rear thumb rest */}
        <g className="injected-syringe-logo__plunger">
          {animated && (
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 0; 18 0; 0 0"
              keyTimes="0;0.42;1"
              calcMode="spline"
              keySplines="0.45 0 0.2 1; 0.45 0 0.2 1"
              dur="5.8s"
              repeatCount="indefinite"
            />
          )}
          <rect x="36" y="196" width="28" height="76" rx="6" fill={ref('plastic')} stroke="rgba(255,255,255,0.12)" strokeWidth="0.75" />
          <rect x="40" y="200" width="8" height="68" rx="3" fill="#ffffff" opacity="0.06" />
          {/* plunger rod */}
          <rect x="60" y="221" width="92" height="12" rx="2" fill={ref('steel')} />
          <rect x="60" y="222.5" width="92" height="2.5" rx="1" fill="#fff" opacity="0.5" />
          <rect x="60" y="229.5" width="92" height="1" fill="#0f172a" opacity="0.35" />
          {/* rubber stopper */}
          <g>
            <rect x="150" y="190" width="18" height="48" rx="3" fill={ref('rubber')} />
            <rect x="153" y="198" width="12" height="1.5" fill="#fff" opacity="0.14" />
            <rect x="153" y="208" width="12" height="1.5" fill="#000" opacity="0.5" />
            <rect x="153" y="220" width="12" height="1.5" fill="#000" opacity="0.4" />
          </g>
        </g>

        {/* Finger flanges — rear of the barrel */}
        <rect x="112" y="156" width="26" height="36" rx="4" fill={ref('plastic')} stroke="rgba(255,255,255,0.1)" strokeWidth="0.6" />
        <rect x="112" y="276" width="26" height="36" rx="4" fill={ref('plastic')} stroke="rgba(255,255,255,0.1)" strokeWidth="0.6" />
        <rect x="116" y="160" width="6" height="28" rx="2" fill="#fff" opacity="0.08" />
        <rect x="116" y="280" width="6" height="28" rx="2" fill="#fff" opacity="0.06" />
        <rect x="118" y="180" width="16" height="68" rx="3" fill={ref('plastic')} />

        {/* Glass barrel */}
        <rect x="128" y="184" width="222" height="60" rx="8" fill="rgba(8,10,14,0.55)" stroke={ref('glass-edge')} strokeWidth="1.15" />
        <rect x="132" y="188" width="214" height="52" rx="6" fill={ref('glass')} />

        {/* Payload */}
        <g clipPath={ref('barrel-clip')}>
          <rect className="injected-syringe-logo__fluid" x="168" y="190" width="168" height="48" fill={ref('fluid')}>
            {animated && (
              <>
                <animate attributeName="x" values="168;186;168" keyTimes="0;0.42;1" calcMode="spline" keySplines="0.45 0 0.2 1; 0.45 0 0.2 1" dur="5.8s" repeatCount="indefinite" />
                <animate attributeName="width" values="168;150;168" keyTimes="0;0.42;1" calcMode="spline" keySplines="0.45 0 0.2 1; 0.45 0 0.2 1" dur="5.8s" repeatCount="indefinite" />
              </>
            )}
          </rect>
          <rect x="168" y="192" width="168" height="7" fill={ref('fluid-sheen')} opacity="0.55">
            {animated && (
              <animate attributeName="x" values="168;186;168" keyTimes="0;0.42;1" calcMode="spline" keySplines="0.45 0 0.2 1; 0.45 0 0.2 1" dur="5.8s" repeatCount="indefinite" />
            )}
          </rect>
          {/* meniscus */}
          <ellipse cx="176" cy="214" rx="7" ry="20" fill="#7f1d1d" opacity="0.55">
            {animated && (
              <animate attributeName="cx" values="176;194;176" keyTimes="0;0.42;1" calcMode="spline" keySplines="0.45 0 0.2 1; 0.45 0 0.2 1" dur="5.8s" repeatCount="indefinite" />
            )}
          </ellipse>
        </g>

        {/* Specular band */}
        <g clipPath={ref('glass-sheen-clip')}>
          <rect x="140" y="194" width="190" height="5" rx="2" fill="#fff" opacity="0.28" />
          <rect className="injected-syringe-logo__sheen" x="120" y="188" width="28" height="52" fill="#fff" opacity="0.14" transform="skewX(-18)">
            {animated && (
              <animate attributeName="x" values="110;300;110" dur="4.6s" repeatCount="indefinite" />
            )}
          </rect>
        </g>
        <rect x="136" y="228" width="200" height="8" rx="2" fill="#000" opacity="0.18" />

        {/* Graduations */}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => {
          const major = i % 2 === 0;
          const x = 188 + i * 13.2;
          return (
            <line
              key={i}
              x1={x}
              y1={196}
              x2={x}
              y2={major ? 206 : 202}
              stroke="rgba(255,255,255,0.42)"
              strokeWidth={major ? 0.9 : 0.55}
            />
          );
        })}
        {['5', '4', '3', '2', '1'].map((n, i) => (
          <text
            key={n}
            x={188 + i * 26.4}
            y="216"
            fill="rgba(255,255,255,0.38)"
            fontSize="7"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {n}
          </text>
        ))}
        <text x="292" y="208" fill="rgba(255,255,255,0.28)" fontSize="6" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
          ml
        </text>

        {/* Luer hub */}
        <path d="M346 202 L378 214 L378 254 L346 266 Z" fill={ref('plastic')} />
        <path d="M346 206 L372 216 L372 252 L346 262 Z" fill={ref('steel-dark')} />
        <ellipse cx="372" cy="234" rx="5" ry="16" fill={ref('steel')} />
        <path d="M350 210 L368 218" stroke="#fff" strokeOpacity="0.2" strokeWidth="0.8" />

        {/* Cannula + bevel */}
        <path
          d="M376 231.15 L498 228.55 L506 233.1 L498 237.7 L376 236.85 Z"
          fill={ref('steel')}
        />
        <path d="M376 232.1 L498 229.7" stroke="#fff" strokeOpacity="0.7" strokeWidth="0.6" strokeLinecap="round" />
        <path d="M498 228.55 L512 232.2 L506 233.1 Z" fill="#f8fafc" />
        <path d="M498 237.7 L512 232.2 L506 233.1 Z" fill="#64748b" />
        <line
          className="injected-syringe-logo__needle-glint"
          x1="420"
          y1="231.4"
          x2="470"
          y2="230.2"
          stroke="#fff"
          strokeWidth="0.7"
          strokeLinecap="round"
        />

        {/* Jet inside the cloud, from the bevel */}
        <path
          className="injected-syringe-logo__stream"
          d="M508 232.4 C534 230 558 236 586 234"
          stroke={ref('jet')}
          strokeWidth="1.35"
          strokeLinecap="round"
        />
        <circle cx="512" cy="233" r="1.6" fill="#fff" opacity="0.8">
          {animated && <animate attributeName="opacity" values="0.25;0.95;0.25" dur="1.6s" repeatCount="indefinite" />}
        </circle>
      </g>
    </svg>
  );
};

export default InjectedSyringeCloudLogo;
